import { catalogFulfilmentModes, catalogItemStatuses, catalogItemTypes, catalogPriceUnits, catalogPricingModels, catalogTagNamespaces, } from "./catalog.js";
import { CATALOG_SEARCH_BOUNDS, } from "./catalog-search.js";
import { foldSearchText, SEARCH_STOP_WORDS, searchTokens } from "./catalog-search-text.js";
import { DomainValidationError } from "./identifiers.js";
// The request of a catalog search, checked and prepared (CHT-710). Pure. `validateCatalogSearchRequest`
// judges one request: a bound that is broken is a `DomainValidationError` (the HTTP schema normally refuses
// it first), and a rule BETWEEN parts is an answer with a reason code (`INVALID` or
// `CONFLICTING_CONSTRAINTS`), so a caller learns which rule failed. `prepareCatalogSearch` turns a valid
// request into what the database statement and the ranking read.
/** The item types each type-bound filter applies to. A filter never touches another type. */
export const PARTY_SIZE_TYPES = Object.freeze(["VILLA", "EXPERIENCE"]);
export const EVENT_WINDOW_TYPES = Object.freeze(["EVENT"]);
const COUNTRY = /^[A-Z]{2}$/;
const CURRENCY = /^[A-Z]{3}$/;
const CODE = /^[a-z0-9_]{1,64}$/;
const TOKEN = /^[\p{L}\p{N}]{1,64}$/u;
const DAY_MS = 86_400_000;
const bad = (field, rule) => new DomainValidationError(field, rule);
function oneOf(field, tuple, value) {
    if (typeof value !== "string" || !tuple.includes(value)) {
        throw bad(field, "must be a known value");
    }
    return value;
}
function list(field, value, max, check) {
    if (value === undefined)
        return undefined;
    if (!Array.isArray(value) || value.length < 1 || value.length > max) {
        throw bad(field, `must list 1 to ${max} values`);
    }
    const seen = new Set();
    const out = [];
    for (const entry of value) {
        const checked = check(entry);
        const key = JSON.stringify(checked);
        if (!seen.has(key)) {
            seen.add(key);
            out.push(checked);
        }
    }
    return out;
}
const finite = (field, value, min, max) => {
    if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
        throw bad(field, `must be a number from ${min} to ${max}`);
    }
    return value;
};
const whole = (field, value, min, max) => {
    const number = finite(field, value, min, max);
    if (!Number.isInteger(number))
        throw bad(field, "must be a whole number");
    return number;
};
const code = (field) => (entry) => {
    if (typeof entry !== "string" || !CODE.test(entry))
        throw bad(field, "must be a lowercase code");
    return entry;
};
function instant(field, value) {
    if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
        throw bad(field, "must be an ISO 8601 instant");
    }
    return value;
}
/** Checks one request. See the file comment: bounds throw, rules between parts answer with a reason. */
export function validateCatalogSearchRequest(request) {
    if (request === null || typeof request !== "object")
        throw bad("request", "must be an object");
    const audience = oneOf("audience", ["CUSTOMER", "STAFF"], request.audience);
    const invalid = [];
    const conflicts = [];
    const staff = request;
    if (audience === "CUSTOMER" &&
        (staff.includeStatuses !== undefined || staff.debug !== undefined)) {
        invalid.push({ code: "staff_field.not_allowed", filters: [] });
    }
    let text;
    let tokenCount = 0;
    if (request.text !== undefined) {
        if (typeof request.text !== "string")
            throw bad("text", "must be text");
        if (Array.from(request.text).length > CATALOG_SEARCH_BOUNDS.textChars) {
            throw bad("text", `must be at most ${CATALOG_SEARCH_BOUNDS.textChars} characters`);
        }
        text = request.text.trim();
        tokenCount = searchTokens(foldSearchText(text)).length;
        if (tokenCount > CATALOG_SEARCH_BOUNDS.textTokens) {
            invalid.push({ code: "text.too_many_tokens", filters: ["text"] });
        }
        if (text === "")
            text = undefined;
    }
    const types = list("types", request.types, catalogItemTypes.length, (entry) => oneOf("types", catalogItemTypes, entry));
    let location;
    if (request.location !== undefined) {
        const { countryCode, locality } = request.location;
        if (countryCode === undefined && locality === undefined) {
            throw bad("location", "needs a countryCode or a locality");
        }
        if (countryCode !== undefined &&
            (typeof countryCode !== "string" || !COUNTRY.test(countryCode))) {
            throw bad("location.countryCode", "must be two capital letters");
        }
        if (locality !== undefined &&
            (typeof locality !== "string" ||
                locality.trim() === "" ||
                Array.from(locality).length > CATALOG_SEARCH_BOUNDS.locality)) {
            throw bad("location.locality", `must be 1 to ${CATALOG_SEARCH_BOUNDS.locality} characters`);
        }
        location = {
            ...(countryCode === undefined ? {} : { countryCode }),
            ...(locality === undefined ? {} : { locality: locality.trim() }),
        };
    }
    let near;
    if (request.near !== undefined) {
        near = {
            latitude: finite("near.latitude", request.near.latitude, -90, 90),
            longitude: finite("near.longitude", request.near.longitude, -180, 180),
            radiusMeters: whole("near.radiusMeters", request.near.radiusMeters, CATALOG_SEARCH_BOUNDS.radiusMinMeters, CATALOG_SEARCH_BOUNDS.radiusMaxMeters),
        };
    }
    let within;
    if (request.within !== undefined) {
        within = {
            south: finite("within.south", request.within.south, -90, 90),
            west: finite("within.west", request.within.west, -180, 180),
            north: finite("within.north", request.within.north, -90, 90),
            east: finite("within.east", request.within.east, -180, 180),
        };
        if (within.south >= within.north) {
            invalid.push({ code: "within.south_not_before_north", filters: ["within"] });
        }
        if (within.west >= within.east) {
            invalid.push({ code: "within.west_not_before_east", filters: ["within"] });
        }
        if (within.north - within.south > CATALOG_SEARCH_BOUNDS.boxMaxDegrees ||
            within.east - within.west > CATALOG_SEARCH_BOUNDS.boxMaxDegrees) {
            invalid.push({ code: "within.too_large", filters: ["within"] });
        }
    }
    let price;
    if (request.price !== undefined) {
        const input = request.price;
        if (typeof input.currency !== "string" || !CURRENCY.test(input.currency)) {
            throw bad("price.currency", "must be three capital letters");
        }
        price = {
            currency: input.currency,
            ...(input.minMinor === undefined
                ? {}
                : { minMinor: whole("price.minMinor", input.minMinor, 0, Number.MAX_SAFE_INTEGER) }),
            ...(input.maxMinor === undefined
                ? {}
                : { maxMinor: whole("price.maxMinor", input.maxMinor, 0, Number.MAX_SAFE_INTEGER) }),
            ...(input.unit === undefined
                ? {}
                : { unit: oneOf("price.unit", catalogPriceUnits, input.unit) }),
            ...(input.includeUnpriced === undefined
                ? {}
                : { includeUnpriced: input.includeUnpriced === true }),
        };
        if (price.minMinor !== undefined &&
            price.maxMinor !== undefined &&
            price.minMinor > price.maxMinor) {
            conflicts.push({ code: "price.min_gt_max", filters: ["price"] });
        }
    }
    const pricingModels = list("pricingModels", request.pricingModels, catalogPricingModels.length, (entry) => oneOf("pricingModels", catalogPricingModels, entry));
    const dietary = list("dietary", request.dietary, CATALOG_SEARCH_BOUNDS.dietary, code("dietary"));
    const categories = list("categories", request.categories, CATALOG_SEARCH_BOUNDS.categories, code("categories"));
    let eventWindow;
    if (request.eventWindow !== undefined) {
        const from = instant("eventWindow.from", request.eventWindow.from);
        const to = instant("eventWindow.to", request.eventWindow.to);
        eventWindow = { from, to };
        const span = Date.parse(to) - Date.parse(from);
        if (span <= 0) {
            conflicts.push({ code: "event_window.from_not_before_to", filters: ["eventWindow"] });
        }
        else if (span > CATALOG_SEARCH_BOUNDS.eventWindowMaxDays * DAY_MS) {
            invalid.push({ code: "event_window.too_long", filters: ["eventWindow"] });
        }
    }
    const partySize = request.partySize === undefined
        ? undefined
        : whole("partySize", request.partySize, 1, CATALOG_SEARCH_BOUNDS.partySizeMax);
    const fulfilmentModes = list("fulfilmentModes", request.fulfilmentModes, catalogFulfilmentModes.length, (entry) => oneOf("fulfilmentModes", catalogFulfilmentModes, entry));
    const preferenceTags = list("preferenceTags", request.preferenceTags, CATALOG_SEARCH_BOUNDS.preferenceTags, (entry) => {
        const tag = entry;
        return {
            namespace: oneOf("preferenceTags.namespace", catalogTagNamespaces, tag?.namespace),
            code: code("preferenceTags.code")(tag?.code),
        };
    });
    let limit = CATALOG_SEARCH_BOUNDS.limitDefault;
    if (request.limit !== undefined) {
        limit = whole("limit", request.limit, 1, CATALOG_SEARCH_BOUNDS.limitMax);
    }
    // Rules that tie a filter to the item types it applies to (never silent: a type it cannot constrain is
    // reported by `prepareCatalogSearch` as `notApplicable`; a request where it can constrain NONE conflicts).
    if (types !== undefined) {
        if (partySize !== undefined && !types.some((type) => PARTY_SIZE_TYPES.includes(type))) {
            conflicts.push({ code: "party_size.not_applicable", filters: ["partySize", "types"] });
        }
        if (eventWindow !== undefined && !types.some((type) => EVENT_WINDOW_TYPES.includes(type))) {
            conflicts.push({ code: "event_window.not_applicable", filters: ["eventWindow", "types"] });
        }
    }
    if (near !== undefined && within !== undefined && invalid.length === 0) {
        const inside = near.latitude >= within.south &&
            near.latitude <= within.north &&
            near.longitude >= within.west &&
            near.longitude <= within.east;
        if (!inside)
            conflicts.push({ code: "near.outside_within", filters: ["near", "within"] });
    }
    if (invalid.length > 0)
        return { ok: false, outcome: "INVALID", reasons: invalid };
    if (conflicts.length > 0) {
        return { ok: false, outcome: "CONFLICTING_CONSTRAINTS", reasons: conflicts };
    }
    let statuses;
    if (audience === "STAFF") {
        statuses = list("includeStatuses", request.includeStatuses, catalogItemStatuses.length, (entry) => oneOf("includeStatuses", catalogItemStatuses, entry));
    }
    const normalized = {
        audience,
        ...(text === undefined ? {} : { text }),
        ...(types === undefined ? {} : { types }),
        ...(location === undefined ? {} : { location }),
        ...(near === undefined ? {} : { near }),
        ...(within === undefined ? {} : { within }),
        ...(price === undefined ? {} : { price }),
        ...(pricingModels === undefined ? {} : { pricingModels }),
        ...(dietary === undefined ? {} : { dietary }),
        ...(categories === undefined ? {} : { categories }),
        ...(eventWindow === undefined ? {} : { eventWindow }),
        ...(partySize === undefined ? {} : { partySize }),
        ...(fulfilmentModes === undefined ? {} : { fulfilmentModes }),
        ...(preferenceTags === undefined ? {} : { preferenceTags }),
        limit,
        ...(audience === "STAFF"
            ? {
                includeStatuses: statuses ?? ["PUBLISHED"],
                ...(staff.debug === true ? { debug: true } : {}),
            }
            : {}),
    };
    return { ok: true, request: normalized };
}
const quote = (token) => `'${token}'`;
/** Prepares a request that `validateCatalogSearchRequest` accepted (and normalized). */
export function prepareCatalogSearch(request) {
    // A control character (a NUL above all) cannot be in a stored name and Postgres refuses it in a text
    // parameter: it becomes a space before folding.
    const folded = request.text === undefined ? "" : foldSearchText(request.text.replace(/\p{Cc}/gu, " "));
    const tokens = searchTokens(folded).filter((token) => TOKEN.test(token));
    const seen = new Set();
    const queryTokens = tokens.filter((token) => {
        if (SEARCH_STOP_WORDS.has(token) || seen.has(token))
            return false;
        seen.add(token);
        return true;
    });
    const text = tokens.length === 0 ? null : { folded, tokens, queryTokens };
    const types = request.types ?? [];
    const effectiveTypes = types.length === 0 ? catalogItemTypes : types;
    const notApplicable = [];
    if (request.partySize !== undefined &&
        effectiveTypes.some((type) => !PARTY_SIZE_TYPES.includes(type))) {
        notApplicable.push("partySize");
    }
    if (request.eventWindow !== undefined &&
        effectiveTypes.some((type) => !EVENT_WINDOW_TYPES.includes(type))) {
        notApplicable.push("eventWindow");
    }
    const active = [];
    if (types.length > 0)
        active.push("types");
    if (request.location !== undefined)
        active.push("location");
    if (request.near !== undefined)
        active.push("near");
    if (request.within !== undefined)
        active.push("within");
    if (request.price !== undefined)
        active.push("price");
    if ((request.pricingModels ?? []).length > 0)
        active.push("pricingModels");
    if ((request.dietary ?? []).length > 0)
        active.push("dietary");
    if ((request.categories ?? []).length > 0)
        active.push("categories");
    if (request.eventWindow !== undefined)
        active.push("eventWindow");
    if (request.partySize !== undefined)
        active.push("partySize");
    if ((request.fulfilmentModes ?? []).length > 0)
        active.push("fulfilmentModes");
    const localityFolded = request.location?.locality === undefined ? "" : foldSearchText(request.location.locality);
    const staffRequest = request;
    return {
        audience: request.audience,
        limit: request.limit ?? CATALOG_SEARCH_BOUNDS.limitDefault,
        text,
        tsquery: {
            phrase: text === null ? null : tokens.map((token) => `${quote(token)}:A`).join(" <-> "),
            prefix: text === null ? null : tokens.map((token) => `${quote(token)}:*A`).join(" & "),
            lexical: queryTokens.length === 0 ? null : queryTokens.map(quote).join(" | "),
        },
        types,
        statuses: request.audience === "STAFF"
            ? (staffRequest.includeStatuses ?? ["PUBLISHED"])
            : ["PUBLISHED"],
        countryCode: request.location?.countryCode ?? null,
        localityKey: localityFolded === "" ? null : localityFolded,
        near: request.near ?? null,
        within: request.within ?? null,
        price: request.price ?? null,
        pricingModels: request.pricingModels ?? [],
        dietary: request.dietary ?? [],
        categories: request.categories ?? [],
        eventWindow: request.eventWindow === undefined
            ? null
            : { from: new Date(request.eventWindow.from), to: new Date(request.eventWindow.to) },
        partySize: request.partySize ?? null,
        fulfilmentModes: request.fulfilmentModes ?? [],
        preferenceTags: request.preferenceTags ?? [],
        notApplicable,
        activeFilters: active,
        pinned: types.length === 1 && localityFolded !== "",
        debug: request.audience === "STAFF" && staffRequest.debug === true,
    };
}
